import { io } from 'socket.io-client'
import { DOCUMENT_RECORDS_NAMESPACE } from '@amitable/shared/realtime-namespaces'
import type { Socket } from 'socket.io-client'
import type { ApolloClient } from '@apollo/client'
import type { Doc } from './types/Doc'
import type {
  CreateDocumentRecordMutation,
  CreateDocumentRecordMutationVariables,
  DeleteDocumentRecordMutation,
  DeleteDocumentRecordMutationVariables,
  DocumentRecordChangesQuery,
  DocumentRecordChangesQueryVariables,
  DocumentRecordQuery,
  DocumentRecordQueryVariables,
  DocumentRecordRevisionsQuery,
  DocumentRecordRevisionsQueryVariables,
  DocumentRecordSyncCursorQuery,
  DocumentRecordSyncCursorQueryVariables,
  DocumentRecordsQuery,
  DocumentRecordsQueryVariables,
  ImportDocumentRecordsMutation,
  ImportDocumentRecordsMutationVariables,
  PatchDocumentRecordMutation,
  PatchDocumentRecordMutationVariables,
} from '@/graphql/documentRecords.gql.generated'
import { resolvePublicApiUrl } from '@/shared/lib/api-url'
import { CUSTOMER_AUTH_SESSION_EXPIRED_EVENT } from '@/shared/lib/customer-auth'
import {
  CreateDocumentRecordDocument,
  DeleteDocumentRecordDocument,
  DocumentRecordChangesDocument,
  DocumentRecordDocument,
  DocumentRecordRevisionsDocument,
  DocumentRecordSyncCursorDocument,
  DocumentRecordsDocument,
  ImportDocumentRecordsDocument,
  PatchDocumentRecordDocument,
} from '@/graphql/documentRecords.gql.generated'

export type RecordChange =
  | { type: 'removed'; documentId: string }
  | { type: 'upserted'; document: Doc }

export class DocumentRecordClient {
  constructor(
    private readonly apollo: ApolloClient,
    private readonly workspaceId: string,
    private readonly socketUrl: string,
  ) {}

  async get(id: string): Promise<Doc> {
    const result = await this.apollo.query<
      DocumentRecordQuery,
      DocumentRecordQueryVariables
    >({
      query: DocumentRecordDocument,
      variables: { id },
      fetchPolicy: 'network-only',
    })

    return toDoc(requireRecord(result.data?.documentRecord))
  }

  async list(documentCollectionId?: string): Promise<Array<Doc>> {
    const docs: Array<Doc> = []
    let after: string | null | undefined
    do {
      const result = await this.apollo.query<
        DocumentRecordsQuery,
        DocumentRecordsQueryVariables
      >({
        query: DocumentRecordsDocument,
        variables: { input: { documentCollectionId, first: 200, after } },
        fetchPolicy: 'network-only',
      })
      const connection = result.data?.documentRecords
      if (!connection) throw new Error('Document records could not be loaded.')
      docs.push(...connection.nodes.map(toDoc))
      after = connection.pageInfo.hasNextPage
        ? connection.pageInfo.endCursor
        : null
    } while (after)

    return docs
  }

  async create(documentCollectionId: string, values: Record<string, unknown>) {
    const result = await this.apollo.mutate<
      CreateDocumentRecordMutation,
      CreateDocumentRecordMutationVariables
    >({
      mutation: CreateDocumentRecordDocument,
      variables: { input: { documentCollectionId, values } },
    })

    return toDoc(requireRecord(result.data?.createDocumentRecord))
  }

  async patch(
    id: string,
    expectedVersion: number,
    patch: Record<string, unknown>,
  ) {
    const result = await this.apollo.mutate<
      PatchDocumentRecordMutation,
      PatchDocumentRecordMutationVariables
    >({
      mutation: PatchDocumentRecordDocument,
      variables: { input: { id, expectedVersion, patch } },
    })

    return toDoc(requireRecord(result.data?.patchDocumentRecord))
  }

  async delete(id: string, expectedVersion: number) {
    await this.apollo.mutate<
      DeleteDocumentRecordMutation,
      DeleteDocumentRecordMutationVariables
    >({
      mutation: DeleteDocumentRecordDocument,
      variables: { input: { id, expectedVersion } },
    })
  }

  async import(
    documentCollectionId: string,
    values: Array<Record<string, unknown>>,
  ) {
    const result = await this.apollo.mutate<
      ImportDocumentRecordsMutation,
      ImportDocumentRecordsMutationVariables
    >({
      mutation: ImportDocumentRecordsDocument,
      variables: {
        inputs: values.map((recordValues) => ({
          documentCollectionId,
          values: recordValues,
        })),
      },
    })

    return (result.data?.importDocumentRecords ?? []).map(toDoc)
  }

  async revisions(recordId: string) {
    const result = await this.apollo.query<
      DocumentRecordRevisionsQuery,
      DocumentRecordRevisionsQueryVariables
    >({
      query: DocumentRecordRevisionsDocument,
      variables: { recordId, first: 100 },
      fetchPolicy: 'network-only',
    })

    return result.data?.documentRecordRevisions.nodes ?? []
  }

  subscribe(
    onChange: (change: RecordChange) => void,
    onError?: (error: unknown) => void,
  ) {
    const state: SubscriptionState = {
      cursor: '0',
      drainAgain: false,
      draining: false,
      knownIds: new Set(),
      socket: null,
      stopped: false,
    }
    const sessionExpiryTarget = typeof window === 'undefined' ? null : window
    const handleSessionExpired = () => {
      state.stopped = true
      state.socket?.disconnect()
      onError?.(new Error('Customer authentication session expired.'))
    }
    sessionExpiryTarget?.addEventListener(
      CUSTOMER_AUTH_SESSION_EXPIRED_EVENT,
      handleSessionExpired,
    )
    void this.initializeSubscription(state, onChange, onError).catch((error) =>
      onError?.(error),
    )

    return () => {
      state.stopped = true
      state.socket?.disconnect()
      sessionExpiryTarget?.removeEventListener(
        CUSTOMER_AUTH_SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      )
    }
  }

  private async initializeSubscription(
    state: SubscriptionState,
    onChange: (change: RecordChange) => void,
    onError?: (error: unknown) => void,
  ) {
    const result = await this.apollo.query<
      DocumentRecordSyncCursorQuery,
      DocumentRecordSyncCursorQueryVariables
    >({ query: DocumentRecordSyncCursorDocument, fetchPolicy: 'network-only' })
    if (this.subscriptionStopped(state)) return
    state.cursor = result.data?.documentRecordSyncCursor ?? '0'
    await this.loadInitialDocuments(state, onChange)
    if (this.subscriptionStopped(state)) return
    const drain = () => void this.drainChanges(state, onChange, onError)
    state.socket = io(
      resolvePublicApiUrl(this.socketUrl, DOCUMENT_RECORDS_NAMESPACE),
      {
        withCredentials: true,
        transports: ['websocket', 'polling'],
      },
    )
    state.socket.on('connect', () => {
      state.socket?.emit('records.subscribe', { workspaceId: this.workspaceId })
      drain()
    })
    state.socket.on('records.changed', drain)
    state.socket.on('connect_error', (error) => onError?.(error))
    drain()
  }

  private async loadInitialDocuments(
    state: SubscriptionState,
    onChange: (change: RecordChange) => void,
  ) {
    for (const document of await this.list()) {
      if (state.stopped) return
      state.knownIds.add(document._id)
      onChange({ type: 'upserted', document })
    }
  }

  private async drainChanges(
    state: SubscriptionState,
    onChange: (change: RecordChange) => void,
    onError?: (error: unknown) => void,
  ) {
    if (state.draining) {
      state.drainAgain = true

      return
    }
    state.draining = true
    try {
      let more: boolean
      do {
        state.drainAgain = false
        more = await this.drainPage(state, onChange)
      } while (more || this.consumeDrainRequest(state))
    } catch (error) {
      onError?.(error)
    } finally {
      state.draining = false
    }
  }

  private async drainPage(
    state: SubscriptionState,
    onChange: (change: RecordChange) => void,
  ) {
    const result = await this.apollo.query<
      DocumentRecordChangesQuery,
      DocumentRecordChangesQueryVariables
    >({
      query: DocumentRecordChangesDocument,
      variables: { after: state.cursor, limit: 200 },
      fetchPolicy: 'network-only',
    })
    const page = result.data?.documentRecordChanges
    if (!page) throw new Error('Document record changes could not be loaded.')
    if (page.resetRequired) {
      await this.resetSubscription(state, onChange, page.cursor)

      return false
    }
    for (const change of page.changes)
      this.applyServerChange(state, onChange, change)

    return page.changes.length === 200
  }

  private async resetSubscription(
    state: SubscriptionState,
    onChange: (change: RecordChange) => void,
    cursor: string,
  ) {
    const documents = await this.list()
    const freshIds = new Set(documents.map((document) => document._id))
    for (const id of state.knownIds)
      if (!freshIds.has(id)) onChange({ type: 'removed', documentId: id })
    state.knownIds.clear()
    for (const document of documents) {
      state.knownIds.add(document._id)
      onChange({ type: 'upserted', document })
    }
    state.cursor = cursor
  }

  private applyServerChange(
    state: SubscriptionState,
    onChange: (change: RecordChange) => void,
    change: DocumentRecordChangesQuery['documentRecordChanges']['changes'][number],
  ) {
    if (BigInt(change.cursor) <= BigInt(state.cursor)) return
    state.cursor = change.cursor
    if (change.type === 'DELETE') {
      state.knownIds.delete(change.recordId)
      onChange({ type: 'removed', documentId: change.recordId })

      return
    }
    if (!change.snapshot) return
    const document = toDoc(change.snapshot as RecordNode)
    state.knownIds.add(document._id)
    onChange({ type: 'upserted', document })
  }

  private subscriptionStopped(state: SubscriptionState) {
    return state.stopped
  }

  private consumeDrainRequest(state: SubscriptionState) {
    const requested = state.drainAgain
    state.drainAgain = false

    return requested
  }
}

type SubscriptionState = {
  cursor: string
  drainAgain: boolean
  draining: boolean
  knownIds: Set<string>
  socket: Socket | null
  stopped: boolean
}

type RecordNode = NonNullable<DocumentRecordQuery['documentRecord']>

function requireRecord(record: RecordNode | null | undefined): RecordNode {
  if (!record) throw new Error('Document record operation returned no record.')

  return record
}

export function toDoc(record: RecordNode): Doc {
  return {
    ...record.values,
    _id: record.id,
    _rev: String(record.version),
    documentCollectionId: record.documentCollectionId,
  }
}
